import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components } from '@api/fast-games/generated';

import { ApiError } from '@libs/errors';
import { ISocketEvents } from '@libs/socket';

import { FriendGamesSocket, IFriendGamesSocketEvents } from '../ws';
import { DELETE, GET, POST } from './clients';

export const FRIENDGAMES_QUERY_KEYS = {
  all: () => ['FRIEND_GAMES'],
  my: (device: string, anonymousSecret: string | null) => [
    'FRIEND_GAMES',
    'my',
    device,
    anonymousSecret,
  ],
  invite: (inviteId: string, deviceId: string, anonymousSecret: string | null) => [
    'FRIEND_GAMES',
    'INVITE',
    inviteId,
    deviceId,
    anonymousSecret,
  ],
};

export function useMyInvitesQuery(
  deviceId: string,
  anonymousSecret: string | null,
  { refetchOnWindowFocus }: { refetchOnWindowFocus?: boolean } = {},
) {
  const qc = useQueryClient();

  return useQuery({
    refetchOnWindowFocus,
    queryKey: FRIENDGAMES_QUERY_KEYS.my(deviceId, anonymousSecret),
    queryFn: async () => {
      const data = (
        await GET('/api/invites/my', {
          params: {
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;

      data.invites.forEach((inv) => {
        qc.setQueryData(FRIENDGAMES_QUERY_KEYS.invite(inv.id, deviceId, anonymousSecret), inv);
      });

      return data;
    },
  });
}

export function useInviteQuery({
  inviteId,
  deviceId,
  anonymousSecret,
  enabled = true,
  staleTime,
  refetchOnWindowFocus,
}: {
  inviteId: string;
  deviceId: string;
  anonymousSecret: string | null;
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}) {
  return useQuery({
    enabled,
    staleTime,
    refetchOnWindowFocus,
    retry: (_, err) => ApiError.canRetry(err),
    queryKey: FRIENDGAMES_QUERY_KEYS.invite(inviteId, deviceId, anonymousSecret),
    queryFn: async () => {
      const data = (
        await GET('/api/invites/{inviteId}', {
          params: {
            path: {
              inviteId,
            },
            query: {
              deviceId,
              anonymousSecret: anonymousSecret ?? undefined,
            },
          },
        })
      ).data!;

      return data;
    },
  });
}

export function useCreateInviteMutation(deviceId: string, anonymousSecret: string | null) {
  const qc = useQueryClient();
  const { onSuccess } = useCommonInviteHooks(deviceId, anonymousSecret);
  const onError = () =>
    qc.invalidateQueries({
      queryKey: FRIENDGAMES_QUERY_KEYS.my(deviceId, anonymousSecret),
    });

  return useMutation({
    mutationFn: async (data: components['schemas']['CreateInviteRequest']) => {
      return (
        await POST('/api/invites', {
          params: {
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
          body: data,
        })
      ).data!;
    },
    onSuccess,
    onError,
  });
}

export function useAcceptInviteMutation(deviceId: string, anonymousSecret: string | null) {
  const { onSuccess, onError } = useCommonInviteHooks(deviceId, anonymousSecret);

  return useMutation({
    mutationFn: async (inviteId: string) => {
      return (
        await POST('/api/invites/{inviteId}/play', {
          params: {
            path: { inviteId },
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;
    },
    onSuccess,
    onError,
  });
}

export function useRejectInviteMutation(deviceId: string, anonymousSecret: string | null) {
  const { onSuccess, onError } = useCommonInviteHooks(deviceId, anonymousSecret);

  return useMutation({
    mutationFn: async (inviteId: string) => {
      return (
        await POST('/api/invites/{inviteId}/reject', {
          params: {
            path: { inviteId },
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;
    },
    onSuccess,
    onError,
  });
}

export function useDeleteInviteMutation(deviceId: string, anonymousSecret: string | null) {
  const qc = useQueryClient();
  const { onError } = useCommonInviteHooks(deviceId, anonymousSecret);
  const onSuccess = () =>
    qc.invalidateQueries({
      queryKey: FRIENDGAMES_QUERY_KEYS.my(deviceId, anonymousSecret),
    });

  return useMutation({
    mutationFn: async (inviteId: string) => {
      return (
        await DELETE('/api/invites/{inviteId}', {
          params: {
            path: { inviteId },
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;
    },
    onSuccess,
    onError,
  });
}

export function useRematchInviteMutation(deviceId: string, anonymousSecret: string | null) {
  const { onSuccess, onError } = useCommonInviteHooks(deviceId, anonymousSecret);

  return useMutation({
    mutationFn: async (inviteId: string) => {
      return (
        await POST('/api/invites/{inviteId}/rematch', {
          params: {
            path: { inviteId },
            query: { deviceId, anonymousSecret: anonymousSecret ?? undefined },
          },
        })
      ).data!;
    },
    onSuccess,
    onError,
  });
}

export function useFriendGamesSocket({
  deviceId,
  anonymousSecret,
  enabled = true,
  cb,
}: {
  deviceId: string;
  anonymousSecret: string | null;
  enabled?: boolean;
  cb?: (e: ISocketEvents<IFriendGamesSocketEvents>) => void;
}) {
  const qc = useQueryClient();

  useSocketInternal({
    deviceId,
    anonymousSecret,
    enabled,
    cb: (e) => {
      cb?.(e);

      if (e.$) return;
      switch (e.t) {
        case 'S':
          {
            const ids = Object.keys(e.s.is);
            ids.forEach((id) => {
              const d = e.s.is[id]!;
              qc.setQueryData(
                FRIENDGAMES_QUERY_KEYS.invite(id, deviceId, anonymousSecret),
                (
                  prev: components['schemas']['InviteDto'] | undefined,
                ): components['schemas']['InviteDto'] | undefined => {
                  if (!prev) return prev;

                  const next = {
                    ...prev,
                    status: d.st,
                    rematchInitiatorSide: d.rs,
                    rematchInviteId: d.rid,
                    expireTimestampMs: d.ex,
                  };

                  return next;
                },
              );
            });
          }
          break;
      }
    },
  });
}

function useSocketInternal({
  deviceId,
  anonymousSecret,
  enabled = true,
  cb,
}: {
  deviceId: string;
  anonymousSecret: string | null;
  enabled?: boolean;
  cb: (e: ISocketEvents<IFriendGamesSocketEvents>) => void;
}) {
  const socketRef = useRef<FriendGamesSocket | null>(null);
  const cbRef = useRef(cb);
  cbRef.current = cb;

  if (!enabled) {
    socketRef.current?.discard();
    socketRef.current = null;
  } else if (
    !socketRef.current ||
    socketRef.current.deviceId !== deviceId ||
    socketRef.current.anonymousSecret !== anonymousSecret
  ) {
    socketRef.current?.discard();
    socketRef.current = new FriendGamesSocket(deviceId, anonymousSecret);
    socketRef.current.subscribe((e) => cbRef.current(e));
  }

  useEffect(
    () => () => {
      socketRef.current?.discard();
    },
    [],
  );
}

function useCommonInviteHooks(deviceId: string, anonymousSecret: string | null) {
  const qc = useQueryClient();

  return {
    onSuccess: (data: components['schemas']['InviteDto']) => {
      qc.setQueryData(FRIENDGAMES_QUERY_KEYS.invite(data.id, deviceId, anonymousSecret), data);
    },

    onError: (err: Error, inviteId: string) => {
      if (!ApiError.canRetry(err)) {
        qc.invalidateQueries({
          queryKey: FRIENDGAMES_QUERY_KEYS.my(deviceId, anonymousSecret),
        });
        qc.invalidateQueries({
          queryKey: FRIENDGAMES_QUERY_KEYS.invite(inviteId, deviceId, anonymousSecret),
        });
      }
    },
  };
}
